import type {
  AstGroup,
  AstNode,
  AstPredicate,
  Logic,
} from '../tools/render/types'

import type { IocAstBuildOptions } from './ioc.ast-kit'
import { mapIocToPredicates } from './ioc.mapping'
import type { IocItem } from './ioc.types'

/**
 * Semantic IOC field → hunt categories routing map. Tool-independent: lists
 * every hunt_category where an IOC of that field is meaningful to hunt.
 *
 * This drives the fanout in `buildIocAstsByCategory` so that e.g. a sha256
 * IOC produces ASTs for process_creation / file_event / image_load /
 * driver_load — covering cases where any consuming tool may carry the field
 * in at least one of those hunt_categories.
 *
 * SIEM tools then prune per-(huntCat × logCat) via CCM_HUNT_CATEGORY_MAP.
 * EDR/XDR tools bypass pruning and emit per-hunt_category their datasource
 * map declares support for. Either way, hunt_categories not supported by the
 * target tool simply produce empty `queries: []` entries.
 */
export const FIELD_TO_CATEGORIES: Record<string, string[]> = {
  md5: ['file_event', 'image_load', 'process_creation'],
  sha256: ['file_event', 'image_load', 'process_creation'],
  target_path: [
    'file_event',
  ],
  process_path: [
    'dns',
    'file_event',
    'image_load',
    'network_connection',
    'pipe_created',
    'process_creation',
    'registry_event',
  ],
  // With the change in the ioc.mapping.ts to split out paths from names
  // this needs to mirror the process_path entry to ensure that basenames still route to process_name when appropriate
  process_name: [
    'dns',
    'file_event',
    'image_load',
    'network_connection',
    'pipe_created',
    'process_creation',
    'registry_event',
  ],
  process_cmdline: ['process_creation'],
  // .dll / .sys / .so / etc. IOCs route here from ioc.mapping.ts. Only
  // image_load carries this semantically — target_path handles the file-write
  // fanout separately for the same IOC.
  object_loaded: ['image_load'],
  url: ['network_connection', 'proxy', 'webserver'],
  domain: ['network_connection', 'proxy', 'webserver'],
  dns_query: ['dns'],
  dns_answer: ['dns'],
  dest_ip: ['firewall', 'network_connection', 'proxy'],
}

/**
 * Deep-clone an AST, dropping predicates whose field is not in `allowed`
 * and collapsing groups that become empty. Returns null when nothing
 * survives (caller should skip emitting a query for that log category).
 */
export function pruneAstByFields(
  node: AstNode,
  allowed: Set<string>,
): AstNode | null {
  if (node.kind === 'pred') {
    return allowed.has(node.field) ? { ...node } : null
  }
  const kept: AstNode[] = []
  for (const child of node.children) {
    const pruned = pruneAstByFields(child, allowed)
    if (pruned) kept.push(pruned)
  }
  if (kept.length === 0) return null
  return { ...node, children: kept }
}

/**
 * Inverse of pruneAstByFields — drop predicates whose field IS in `excluded`.
 * Used by the EDR/XDR render path to strip CCM fields that a tool's event
 * types don't carry for a given hunt_category (per datasource.unsupportedFields).
 */
export function pruneAstByExcludedFields(
  node: AstNode,
  excluded: Set<string>,
): AstNode | null {
  if (excluded.size === 0) return node
  if (node.kind === 'pred') {
    return excluded.has(node.field) ? null : { ...node }
  }
  const kept: AstNode[] = []
  for (const child of node.children) {
    const pruned = pruneAstByExcludedFields(child, excluded)
    if (pruned) kept.push(pruned)
  }
  if (kept.length === 0) return null
  return { ...node, children: kept }
}

/**
 * Given a list of CCM field names (from built buckets), return the unique sorted
 * set of categories implied by those fields.
 */
export function deriveCategories(fields: string[]): string[] {
  const cats = new Set<string>()
  for (const f of fields) {
    for (const cat of FIELD_TO_CATEGORIES[f] ?? []) {
      cats.add(cat)
    }
  }
  return [...cats].sort()
}

type Key = string // `${field}|${op}`
type Bucket = {
  field: string
  op: AstPredicate['op']
  values: string[]
  valueType: AstPredicate['valueType']
}

/** Shared bucket-building logic extracted for reuse. */
export function buildBuckets(iocs: IocItem[]): Map<Key, Bucket> {
  const buckets = new Map<Key, Bucket>()
  for (const ioc of iocs) {
    const preds = mapIocToPredicates(ioc).filter((p) =>
      Array.isArray(p.value) ? p.value.length > 0 : p.value !== undefined,
    )
    for (const p of preds) {
      const key: Key = `${p.field}|${p.op}`
      let bucket = buckets.get(key)
      if (!bucket) {
        bucket = {
          field: p.field,
          op: p.op,
          values: [],
          valueType: p.valueType,
        }
        buckets.set(key, bucket)
      }
      if (Array.isArray(p.value)) {
        for (const v of p.value) {
          if (
            typeof v === 'string' &&
            v.length > 0 &&
            !bucket.values.includes(v)
          ) {
            bucket.values.push(v)
          }
        }
      } else if (typeof p.value === 'string' && p.value.length > 0) {
        if (!bucket.values.includes(p.value)) bucket.values.push(p.value)
      }
    }
  }
  return buckets
}

/** Convert a bucket to a single-pred AND subgroup (same shape as buildIocAst children). */
function bucketToGroup(bucket: Bucket): AstGroup {
  const pred: AstPredicate = {
    kind: 'pred',
    field: bucket.field,
    op: bucket.op,
    value: bucket.values.length === 1 ? bucket.values[0] : bucket.values,
    valueType: bucket.valueType,
  }
  return { kind: 'group', logic: 'and', children: [pred] }
}

/**
 * Build one filtered AstGroup per applicable category.
 * Each group contains only the predicates whose fields belong to that category,
 * and has datasource.category + dataDisplay directives attached.
 *
 * Categories with no matching predicates are omitted from the returned Map.
 */
export function buildIocAstsByCategory(
  iocs: IocItem[],
  opts?: IocAstBuildOptions,
): Map<string, AstGroup> {
  const topLogic: Logic = opts?.topLevelLogic ?? 'or'
  const buckets = buildBuckets(iocs)

  // Group bucket keys by category
  const categoryBuckets = new Map<string, Bucket[]>()
  for (const bucket of buckets.values()) {
    for (const cat of FIELD_TO_CATEGORIES[bucket.field] ?? []) {
      const list = categoryBuckets.get(cat) ?? []
      list.push(bucket)
      categoryBuckets.set(cat, list)
    }
  }

  const result = new Map<string, AstGroup>()
  for (const [cat, catBuckets] of [...categoryBuckets.entries()].sort(
    ([a], [b]) => a.localeCompare(b),
  )) {
    const children = catBuckets
      .sort(
        (a, b) => a.field.localeCompare(b.field) || a.op.localeCompare(b.op),
      )
      .map(bucketToGroup)

    const ast: AstGroup = {
      kind: 'group',
      logic: topLogic,
      children,
      directives: {
        datasource: { category: [cat] },
        dataDisplay: { activeView: 'detail' },
      },
    }
    result.set(cat, ast)
  }
  return result
}
